import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
});

// Add OIDC access token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors with automatic token refresh
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
      return Promise.reject(new Error('Network Error'));
    }

    // Handle server errors
    if (error.response.status >= 500) {
      console.error('Server error:', error.response.data);
      return Promise.reject(new Error('Server Error. Please try again later.'));
    }

    // Handle banned user
    if (error.response.status === 403 && error.response.data?.banned) {
      const { reason, ban_id, banned_at } = error.response.data;
      window.dispatchEvent(new CustomEvent('erozion-banned', { detail: { reason, ban_id, banned_at } }));
      return Promise.reject(error);
    }

    // Handle temporarily suspended user
    if (error.response.status === 403 && error.response.data?.suspended) {
      const { until, moderation_id } = error.response.data;
      window.dispatchEvent(new CustomEvent('erozion-suspended', { detail: { until, moderation_id } }));
      return Promise.reject(error);
    }

    // Handle 401 — attempt token refresh
    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const userId = localStorage.getItem('user_id');
      if (!userId) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_id');
        processQueue(new Error('No user_id'), null);
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh`, { user_id: userId });
        localStorage.setItem('access_token', data.access_token);
        processQueue(null, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_id');
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth API — OIDC flows
export const authAPI = {
  // Exchange authorization code for tokens (called after OIDC redirect)
  callback: async (code: string, codeVerifier: string, redirectUri: string) => {
    const response = await api.post('/auth/callback', {
      code,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri,
    });
    return response.data;
  },

  // Refresh access token
  refresh: async (userId: string) => {
    const response = await api.post('/auth/refresh', { user_id: userId });
    return response.data;
  },

  // Logout
  logout: async (userId: string) => {
    const response = await api.post('/auth/logout', { user_id: userId });
    return response.data;
  },

  // Get OIDC configuration
  getOIDCConfig: async () => {
    const response = await api.get('/auth/oidc-config');
    return response.data;
  },
};

// User API
export const userAPI = {
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  getUserProfile: async (userId: string) => {
    const response = await api.get(`/users/${userId}/profile`);
    return response.data;
  },

  findUserByUsername: async (username: string) => {
    try {
      const response = await api.get(`/users/search?username=${encodeURIComponent(username)}`);
      return response.data;
    } catch (error) {
      console.warn(`Recherche d'utilisateur par nom échouée: ${username}`, error);
      
      // Solution temporaire: retourner un résultat simulé pour éviter de casser l'UI
      // À supprimer quand l'API sera disponible
      if (username.length >= 3) {
        return { 
          id: `temp-${username}`,
          username: username,
          avatar_url: null
        };
      }
      
      // Si trop court, considérer comme invalide
      return null;
    }
  },
  
  updateProfile: async (updates: any) => {
    const response = await api.put('/users/profile', updates);
    return response.data;
  }
};

// Server API
export const serverAPI = {
  getServers: async () => {
    const response = await api.get('/servers');
    return response.data;
  },
  
  inviteUserToServer: async (serverId: string, username: string) => {
    const response = await api.post(`/servers/${serverId}/invite`, { username });
    return response.data;
  },
  
  createServer: async (name: string, description?: string, iconUrl?: string) => {
    const response = await api.post('/servers', { name, description, iconUrl });
    return response.data;
  },
  
  getServer: async (serverId: string) => {
    const response = await api.get(`/servers/${serverId}`);
    return response.data;
  },

  joinServer: async (serverId: string) => {
    const response = await api.post(`/servers/${serverId}/join`);
    return response.data;
  },
  
  updateServer: async (serverId: string, updates: any) => {
    const response = await api.put(`/servers/${serverId}`, updates);
    return response.data;
  },
  
  getChannels: async (serverId: string) => {
    const response = await api.get(`/servers/${serverId}/channels`);
    return response.data;
  },
  
  createChannel: async (serverId: string, name: string, description?: string, type?: string, isPrivate?: boolean, parentId?: string | null) => {
    const response = await api.post(`/servers/${serverId}/channels`, { 
      name, 
      description, 
      type, 
      is_private: isPrivate,
      parent_id: parentId || null,
    });
    return response.data;
  },

  updateChannel: async (channelId: string, updates: any) => {
    const response = await api.put(`/channels/${channelId}`, updates);
    return response.data;
  },

  deleteChannel: async (channelId: string) => {
    const response = await api.delete(`/channels/${channelId}`);
    return response.data;
  },

  reorderChannels: async (serverId: string, items: Array<{ id: string; parent_id?: string | null; position: number; sync_permissions?: boolean }>) => {
    const response = await api.put(`/servers/${serverId}/channels/reorder`, { items });
    return response.data;
  },

  syncChannelPermissions: async (channelId: string) => {
    const response = await api.post(`/channels/${channelId}/permissions/sync`);
    return response.data;
  },
  
  getServerMembers: async (serverId: string) => {
    const response = await api.get(`/servers/${serverId}/members`);
    return response.data;
  },
  
  updateMemberRole: async (serverId: string, userId: string, role: string) => {
    const response = await api.put(`/servers/${serverId}/members/${userId}/role`, { role });
    return response.data;
  },
  
  getRoles: async (serverId: string) => {
    const response = await api.get(`/servers/${serverId}/roles`);
    return response.data;
  },
  
  createRole: async (serverId: string, name: string, color?: string, permissions?: any) => {
    const response = await api.post(`/servers/${serverId}/roles`, { name, color, permissions });
    return response.data;
  },
  
  updateRole: async (serverId: string, roleId: string, updates: any) => {
    const response = await api.put(`/servers/${serverId}/roles/${roleId}`, updates);
    return response.data;
  },
  
  deleteRole: async (serverId: string, roleId: string) => {
    const response = await api.delete(`/servers/${serverId}/roles/${roleId}`);
    return response.data;
  },
  
  assignRole: async (serverId: string, userId: string, roleId: string) => {
    const response = await api.post(`/servers/${serverId}/members/${userId}/roles/${roleId}`);
    return response.data;
  },
  
  removeRole: async (serverId: string, userId: string, roleId: string) => {
    const response = await api.delete(`/servers/${serverId}/members/${userId}/roles/${roleId}`);
    return response.data;
  },

  leaveServer: async (serverId: string, userId: string) => {
    const response = await api.delete(`/servers/${serverId}/members/${userId}`);
    return response.data;
  }
};

// Invitation API
export const invitationAPI = {
  // Generate a new invitation link
  createInvitation: async (serverId: string, maxUses: number = 0, maxAge: number = 0) => {
    const response = await api.post(`/servers/${serverId}/invites`, {
      max_uses: maxUses, // 0 for unlimited
      max_age: maxAge    // 0 for never expires (in seconds)
    });
    return response.data;
  },
  
  // Get information about an invitation
  getInvitationInfo: async (inviteCode: string) => {
    const response = await api.get(`/invites/${inviteCode}`);
    return response.data;
  },
  
  // Accept an invitation
  acceptInvitation: async (inviteCode: string) => {
    const response = await api.post(`/invites/${inviteCode}/accept`);
    return response.data;
  },
  
  // Get all invitations for a server
  getServerInvitations: async (serverId: string) => {
    const response = await api.get(`/servers/${serverId}/invites`);
    return response.data;
  },
  
  // Revoke an invitation
  revokeInvitation: async (inviteCode: string) => {
    const response = await api.delete(`/invites/${inviteCode}`);
    return response.data;
  }
};

// Friends API
export const friendsAPI = {
  getFriends: async () => {
    const response = await api.get('/friends');
    return response.data;
  },
  
  getPendingRequests: async () => {
    const response = await api.get('/friends/pending');
    return response.data;
  },
  
  sendFriendRequest: async (username: string) => {
    const response = await api.post('/friends', { username });
    return response.data;
  },
  
  acceptFriendRequest: async (requestId: string) => {
    const response = await api.put(`/friends/${requestId}/accept`);
    return response.data;
  },
  
  rejectFriendRequest: async (requestId: string) => {
    const response = await api.put(`/friends/${requestId}/reject`);
    return response.data;
  },
  
  removeFriend: async (friendId: string) => {
    const response = await api.delete(`/friends/${friendId}`);
    return response.data;
  }
};

// Messages API
export const messagesAPI = {
  getChannelMessages: async (
    channelId: string,
    options?: { limit?: number; before?: string }
  ) => {
    const response = await api.get(`/channels/${channelId}/messages`, {
      params: {
        limit: options?.limit,
        before: options?.before,
      },
    });
    return response.data;
  },
  
  getDirectMessages: async (
    userId: string,
    options?: { limit?: number; before?: string }
  ) => {
    const response = await api.get(`/direct-messages/${userId}`, {
      params: {
        limit: options?.limit,
        before: options?.before,
      },
    });
    return response.data;
  },
  
  // Messages are sent via Phoenix WebSocket channels — no POST endpoints.
  // Edit, delete, and reactions also go through Phoenix WebSocket.
  // See src/lib/realtime.ts: sendChannelMessage(), editMessage(), deleteMessage(), addReaction(), removeReaction()

  getUnreadDms: async () => {
    const response = await api.get('/direct-messages/unread');
    return response.data;
  },

  getConversations: async () => {
    const response = await api.get('/direct-messages/conversations');
    return response.data;
  },

  markDmAsRead: async (userId: string) => {
    const response = await api.post(`/direct-messages/${userId}/read`);
    return response.data;
  },

  getChannelApplicationCommands: async (channelId: string) => {
    const response = await api.get(`/channels/${channelId}/application-commands`);
    return response.data;
  },

  createChannelInteraction: async (channelId: string, payload: {
    application_id: string;
    command_id?: string;
    message_id?: string;
    custom_id?: string;
    component_type?: number;
    type?: number;
    data?: Record<string, any>;
  }) => {
    const response = await api.post(`/channels/${channelId}/interactions`, payload);
    return response.data;
  },
};

// Notification API
export const notificationAPI = {
  // Récupérer toutes les notifications
  getNotifications: async () => {
    const response = await api.get('/notifications');
    return response.data;
  },

  // Obtenir le compte des notifications non lues
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread/count');
    return response.data.count;
  },

  // Marquer une notification comme lue
  markAsRead: async (notificationId: string) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Marquer toutes les notifications comme lues
  markAllAsRead: async () => {
    const response = await api.put('/notifications/read/all');
    return response.data;
  },

  // Supprimer une notification
  deleteNotification: async (notificationId: string) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },

  // Supprimer toutes les notifications
  deleteAllNotifications: async () => {
    const response = await api.delete('/notifications/all');
    return response.data;
  }
};

// Upload API
export const uploadAPI = {
  /** Upload user avatar (returns { url }) */
  uploadAvatar: async (file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/uploads/avatar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  },

  /** Upload user banner (returns { url }) */
  uploadBanner: async (file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/uploads/banner', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  },

  /** Upload server icon (returns { url }) */
  uploadServerIcon: async (serverId: string, file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/uploads/server/${serverId}/icon`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  },

  /** Upload server banner (returns { url }) */
  uploadServerBanner: async (serverId: string, file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/uploads/server/${serverId}/banner`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  },

  /** Upload game-server icon (returns { url }) */
  uploadGameServerIcon: async (serverId: string, file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/uploads/server/${serverId}/game-icon`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  },
};

// Hosting API (Minecraft server hosting)
export const hostingAPI = {
  // Plans
  getPlans: async () => {
    const response = await api.get('/hosting/plans');
    return response.data;
  },

  // Eggs (dynamic from Pterodactyl nest)
  getEggs: async () => {
    const response = await api.get('/hosting/eggs');
    return response.data;
  },

  // Locations (dynamic from Pterodactyl)
  getLocations: async () => {
    const response = await api.get('/hosting/locations');
    return response.data;
  },

  // Servers
  getServers: async () => {
    const response = await api.get('/hosting/servers');
    return response.data;
  },
  getServer: async (id: string) => {
    const response = await api.get(`/hosting/servers/${id}`);
    return response.data;
  },
  createServer: async (data: { name: string; serverType: string; mcVersion: string; planId?: string; description?: string; erozionServerId?: string; eggId?: number; locationId?: number; eulaAccepted?: boolean }) => {
    const response = await api.post('/hosting/servers', data);
    return response.data;
  },
  updateServer: async (id: string, data: { name?: string; description?: string; maxPlayers?: number; erozionServerId?: string | null }) => {
    const response = await api.put(`/hosting/servers/${id}`, data);
    return response.data;
  },
  deleteServer: async (id: string) => {
    const response = await api.delete(`/hosting/servers/${id}`);
    return response.data;
  },

  // Power
  power: async (id: string, action: 'start' | 'stop' | 'restart' | 'kill') => {
    const response = await api.post(`/hosting/servers/${id}/power`, { action });
    return response.data;
  },

  // Console command (REST fallback)
  sendCommand: async (id: string, command: string) => {
    const response = await api.post(`/hosting/servers/${id}/command`, { command });
    return response.data;
  },

  // Backups
  getBackups: async (serverId: string) => {
    const response = await api.get(`/hosting/servers/${serverId}/backups`);
    return response.data;
  },
  createBackup: async (serverId: string, name?: string) => {
    const response = await api.post(`/hosting/servers/${serverId}/backups`, { name });
    return response.data;
  },
  deleteBackup: async (serverId: string, backupId: string) => {
    const response = await api.delete(`/hosting/servers/${serverId}/backups/${backupId}`);
    return response.data;
  },
  getBackupDownloadUrl: async (serverId: string, backupId: string) => {
    const response = await api.get(`/hosting/servers/${serverId}/backups/${backupId}/download`);
    return response.data;
  },

  // Sub-users
  getSubusers: async (serverId: string) => {
    const response = await api.get(`/hosting/servers/${serverId}/subusers`);
    return response.data;
  },
  addSubuser: async (serverId: string, userId: string, permissions: number) => {
    const response = await api.post(`/hosting/servers/${serverId}/subusers`, { userId, permissions });
    return response.data;
  },
  updateSubuser: async (serverId: string, subuserId: string, permissions: number) => {
    const response = await api.put(`/hosting/servers/${serverId}/subusers/${subuserId}`, { permissions });
    return response.data;
  },
  removeSubuser: async (serverId: string, subuserId: string) => {
    const response = await api.delete(`/hosting/servers/${serverId}/subusers/${subuserId}`);
    return response.data;
  },

  // EULA
  getEulaStatus: async (serverId: string) => {
    const response = await api.get(`/hosting/servers/${serverId}/eula`);
    return response.data as { eulaAccepted: boolean };
  },
  acceptEula: async (serverId: string) => {
    const response = await api.post(`/hosting/servers/${serverId}/eula/accept`);
    return response.data;
  },

  // File management
  listFiles: async (serverId: string, dir = '/') => {
    const response = await api.get(`/hosting/servers/${serverId}/files`, { params: { dir } });
    return response.data as {
      dir: string;
      files: Array<{
        name: string;
        mode: string;
        size: number;
        isFile: boolean;
        isSymlink: boolean;
        mimetype: string;
        createdAt: string;
        modifiedAt: string | null;
      }>;
    };
  },
  getFileContent: async (serverId: string, file: string) => {
    const response = await api.get(`/hosting/servers/${serverId}/files/content`, { params: { file } });
    return response.data as { file: string; content: string };
  },
  writeFileContent: async (serverId: string, file: string, content: string) => {
    const response = await api.put(`/hosting/servers/${serverId}/files/content`, { file, content });
    return response.data;
  },
  renameFiles: async (serverId: string, dir: string, files: Array<{ from: string; to: string }>) => {
    const response = await api.post(`/hosting/servers/${serverId}/files/rename`, { dir, files });
    return response.data;
  },
  deleteFiles: async (serverId: string, dir: string, files: string[]) => {
    const response = await api.post(`/hosting/servers/${serverId}/files/delete`, { dir, files });
    return response.data;
  },
  compressFiles: async (serverId: string, dir: string, files: string[]) => {
    const response = await api.post(`/hosting/servers/${serverId}/files/compress`, { dir, files });
    return response.data;
  },
  decompressFile: async (serverId: string, dir: string, file: string) => {
    const response = await api.post(`/hosting/servers/${serverId}/files/decompress`, { dir, file });
    return response.data;
  },
  getFileDownloadUrl: async (serverId: string, file: string) => {
    const response = await api.get(`/hosting/servers/${serverId}/files/download`, { params: { file } });
    return response.data as { url: string };
  },
  getFileUploadUrl: async (serverId: string, dir = '/') => {
    const response = await api.get(`/hosting/servers/${serverId}/files/upload-url`, { params: { dir } });
    return response.data as { url: string };
  },
  createFolder: async (serverId: string, dir: string, name: string) => {
    const response = await api.post(`/hosting/servers/${serverId}/files/create-folder`, { dir, name });
    return response.data;
  },
};

// Plugin/mod browser API (Modrinth proxy)
export const pluginAPI = {
  search: async (serverId: string, query: string, offset = 0, limit = 20) => {
    const response = await api.get(`/hosting/servers/${serverId}/plugins/search`, {
      params: { q: query, offset, limit },
    });
    return response.data as {
      hits: Array<{
        slug: string;
        project_id: string;
        title: string;
        description: string;
        icon_url: string | null;
        downloads: number;
        follows: number;
        categories: string[];
        project_type: string;
        author: string;
        date_modified: string;
      }>;
      total_hits: number;
      offset: number;
      limit: number;
    };
  },
  getVersions: async (serverId: string, projectId: string) => {
    const response = await api.get(`/hosting/servers/${serverId}/plugins/${projectId}/versions`);
    return response.data as Array<{
      id: string;
      version_number: string;
      name: string;
      changelog: string | null;
      date_published: string;
      files: Array<{ filename: string; size: number; primary: boolean }>;
      game_versions: string[];
      loaders: string[];
    }>;
  },
  install: async (serverId: string, versionId: string) => {
    const response = await api.post(`/hosting/servers/${serverId}/plugins/install`, { versionId });
    return response.data as { message: string; filename: string; folder: string; size: number };
  },
  getInstalled: async (serverId: string) => {
    const response = await api.get(`/hosting/servers/${serverId}/plugins/installed`);
    return response.data as { folder: string; files: Array<{ name: string; size: number; modifiedAt: string }> };
  },
  deleteInstalled: async (serverId: string, filename: string) => {
    const response = await api.delete(`/hosting/servers/${serverId}/plugins/installed/${encodeURIComponent(filename)}`);
    return response.data;
  },
};

export default api;
